import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, Button, Stack, Chip, Avatar } from '@mui/joy';
import ArrowBackRoundedIcon  from '@mui/icons-material/ArrowBackRounded';
import RestoreRoundedIcon    from '@mui/icons-material/RestoreRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';

import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import { AppContext } from '../../context';
import {
    APIGetTrashedEmployees, APIRestoreFromTrash,
    APIDeleteEmployeePermanent, parseApiError,
} from '../../api';


export default function TrashedEmployeesPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId]   = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const data = await APIGetTrashedEmployees(token);
        if (Array.isArray(data)) setItems(data);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    const handleRestore = async (item) => {
        setBusyId(item.id);
        const res = await APIRestoreFromTrash(token, item.id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка восстановления'), 'danger');
        } else {
            showToast('Восстановлен ✓', 'success');
            loadAll();
        }
    };

    const handleDeletePermanent = async (item) => {
        // Тройное подтверждение для финального удаления
        if (!window.confirm(`Удалить ${item.full_name} НАВСЕГДА?\n\nЭто действие необратимо.`)) return;
        if (!window.confirm(`Подтвердите ещё раз: ${item.email}\n\nВсе данные сотрудника будут удалены.`)) return;
        setBusyId(item.id);
        const res = await APIDeleteEmployeePermanent(token, item.id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка удаления'), 'danger');
        } else {
            showToast('Удалён навсегда', 'warning');
            loadAll();
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button
                        variant="plain"
                        startDecorator={<ArrowBackRoundedIcon />}
                        onClick={() => navigate('/employees')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}
                    >
                        Назад
                    </Button>
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                        🗑 Корзина сотрудников
                    </Typography>
                </Box>

                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                    Сотрудники в корзине находятся месяц, после чего удаляются навсегда.
                </Typography>

                {loading ? (
                    <MemeLoader />
                ) : items.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'var(--joy-palette-text-tertiary)' }}>
                        Корзина пуста
                    </Box>
                ) : (
                    <Stack spacing={1}>
                        {items.map(item => (
                            <Box key={item.id} sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                borderRadius: 'var(--joy-radius-lg)',
                                p: 2, display: 'flex',
                                alignItems: 'center', gap: 1.5,
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            }}>
                                <Avatar src={item.avatar_url} size="sm" sx={{
                                    width: 34, height: 34, fontSize: 12, fontWeight: 700,
                                    bgcolor: 'var(--joy-palette-background-level3)', color: 'var(--joy-palette-text-tertiary)',
                                    border: '1px solid var(--joy-palette-divider)', flexShrink: 0,
                                }}>
                                    {(item.full_name || '').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
                                        {item.full_name}
                                    </Typography>
                                    <Typography noWrap sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.email} · {item.role}
                                    </Typography>
                                    {item.trash_reason && (
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                            Причина: {item.trash_reason}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontSize: 11, color: '#f59e0b', mt: 0.3, fontWeight: 600 }}>
                                        {item.days_left > 0
                                            ? `Удалится через ${item.days_left} дн.`
                                            : 'Удалится сегодня'
                                        }
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5} sx={{
                                    flexShrink: 0,
                                    flexWrap: 'wrap',
                                    width: { xs: '100%', sm: 'auto' },
                                    justifyContent: 'flex-end',
                                }}>
                                    <Button
                                        size="sm"
                                        variant="soft" color="success"
                                        startDecorator={<RestoreRoundedIcon sx={{ fontSize: 16 }} />}
                                        loading={busyId === item.id}
                                        onClick={() => handleRestore(item)}
                                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                    >
                                        Восстановить
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="soft" color="danger"
                                        startDecorator={<DeleteForeverRoundedIcon sx={{ fontSize: 16 }} />}
                                        loading={busyId === item.id}
                                        onClick={() => handleDeletePermanent(item)}
                                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                                    >
                                        Навсегда
                                    </Button>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}